'use client'

import React, { useMemo, useState } from 'react'
import { useChain } from '@azuro-org/sdk'
import { useAccount } from '@azuro-org/sdk-social-aa-connector'
import { waitForTransactionReceipt } from '@wagmi/core'
import cx from 'classnames'
import {
  type Abi,
  type Address,
  erc20Abi,
  formatUnits,
  isAddressEqual,
  maxUint256,
  parseUnits,
  zeroAddress,
} from 'viem'
import { useConfig, useReadContract, useWriteContract } from 'wagmi'

import { Button, Input } from 'components/inputs'
import { Icon } from 'components/ui'
import ConnectButtonWrapper from 'compositions/ConnectButtonWrapper/ConnectButtonWrapper'


const fixedMathOne = 1_000_000_000_000n

const formatAmount = (value?: bigint, decimals = 18, digits = 4) => {
  if (typeof value === 'undefined') {
    return '-'
  }

  const formatted = Number(formatUnits(value, decimals))

  if (!Number.isFinite(formatted)) {
    return formatUnits(value, decimals)
  }

  return formatted.toLocaleString(undefined, {
    maximumFractionDigits: digits,
  })
}

const formatHash = (hash?: string) => {
  if (!hash) {
    return ''
  }

  return `${hash.slice(0, 10)}...${hash.slice(-8)}`
}

const useParsedUnits = (value: string, decimals: number) => {
  return useMemo(() => {
    if (!value) {
      return 0n
    }

    try {
      return parseUnits(value, decimals)
    }
    catch {
      return 0n
    }
  }, [ value, decimals ])
}

type StatProps = {
  label: string
  value: React.ReactNode
}

const Stat: React.FC<StatProps> = ({ label, value }) => (
  <div className="rounded-md border border-grey-10 bg-bg-l2 p-3">
    <div className="text-caption-12 text-grey-60">{label}</div>
    <div className="mt-1 text-caption-14 font-semibold text-grey-90 break-words">{value}</div>
  </div>
)

const LiquidityPage: React.FC = () => {
  const { address } = useAccount()
  const { appChain, contracts, betToken, isRightNetwork } = useChain()
  const wagmiConfig = useConfig()
  const writeContract = useWriteContract()
  const wagmiChainId = appChain.id as typeof wagmiConfig.chains[number]['id']

  const lpAddress = contracts.lp.address as Address
  const lpAbi = contracts.lp.abi as Abi
  const tokenAddress = betToken.address as Address

  const [ amount, setAmount ] = useState('')
  const [ hookData, setHookData ] = useState('0x')
  const [ depositId, setDepositId ] = useState('')
  const [ withdrawPercent, setWithdrawPercent ] = useState('100')
  const [ txHash, setTxHash ] = useState<string>()
  const [ status, setStatus ] = useState<string>()
  const [ error, setError ] = useState<string>()

  const amountRaw = useParsedUnits(amount, betToken.decimals)
  const withdrawPercentRaw = useParsedUnits(withdrawPercent, 10)
  const depositIdRaw = depositId ? BigInt(depositId) : undefined

  const balanceQuery = useReadContract({
    address: tokenAddress,
    abi: erc20Abi,
    functionName: 'balanceOf',
    chainId: wagmiChainId,
    args: [ address! ],
    query: {
      enabled: Boolean(address),
    },
  })

  const allowanceQuery = useReadContract({
    address: tokenAddress,
    abi: erc20Abi,
    functionName: 'allowance',
    chainId: wagmiChainId,
    args: [ address!, lpAddress ],
    query: {
      enabled: Boolean(address),
    },
  })

  const reserveQuery = useReadContract({
    address: lpAddress,
    abi: lpAbi,
    functionName: 'getReserve',
    chainId: wagmiChainId,
  })

  const minDepoQuery = useReadContract({
    address: lpAddress,
    abi: lpAbi,
    functionName: 'minDepo',
    chainId: wagmiChainId,
  })

  const lastDepositQuery = useReadContract({
    address: lpAddress,
    abi: lpAbi,
    functionName: 'getLastDepositId',
    chainId: wagmiChainId,
  })

  const lpNftBalanceQuery = useReadContract({
    address: lpAddress,
    abi: lpAbi,
    functionName: 'balanceOf',
    chainId: wagmiChainId,
    args: [ address! ],
    query: {
      enabled: Boolean(address),
    },
  })

  const liquidityManagerQuery = useReadContract({
    address: lpAddress,
    abi: lpAbi,
    functionName: 'liquidityManager',
    chainId: wagmiChainId,
  })

  const withdrawableQuery = useReadContract({
    address: lpAddress,
    abi: lpAbi,
    functionName: 'nodeWithdrawView',
    chainId: wagmiChainId,
    args: depositIdRaw ? [ depositIdRaw ] : undefined,
    query: {
      enabled: Boolean(depositIdRaw),
    },
  })

  const withdrawAfterQuery = useReadContract({
    address: lpAddress,
    abi: lpAbi,
    functionName: 'withdrawAfter',
    chainId: wagmiChainId,
    args: depositIdRaw ? [ depositIdRaw ] : undefined,
    query: {
      enabled: Boolean(depositIdRaw),
    },
  })

  const depositOwnerQuery = useReadContract({
    address: lpAddress,
    abi: lpAbi,
    functionName: 'ownerOf',
    chainId: wagmiChainId,
    args: depositIdRaw ? [ depositIdRaw ] : undefined,
    query: {
      enabled: Boolean(depositIdRaw),
      retry: false,
    },
  })

  const isAllowanceEnough = typeof allowanceQuery.data === 'bigint' && allowanceQuery.data >= amountRaw
  const isBalanceEnough = typeof balanceQuery.data === 'bigint' && balanceQuery.data >= amountRaw
  const isBelowMinDeposit = typeof minDepoQuery.data === 'bigint' && amountRaw > 0n && amountRaw < minDepoQuery.data
  const isManagerEnabled = liquidityManagerQuery.data
    ? !isAddressEqual(liquidityManagerQuery.data as Address, zeroAddress)
    : false
  const isDepositOwner = address && depositOwnerQuery.data
    ? isAddressEqual(address, depositOwnerQuery.data as Address)
    : false

  const refreshReads = () => {
    balanceQuery.refetch()
    allowanceQuery.refetch()
    reserveQuery.refetch()
    lastDepositQuery.refetch()
    lpNftBalanceQuery.refetch()
    withdrawableQuery.refetch()
    withdrawAfterQuery.refetch()
    depositOwnerQuery.refetch()
  }

  const runTx = async (label: string, action: () => Promise<`0x${string}`>) => {
    setError(undefined)
    setStatus(`${label} transaction pending`)
    setTxHash(undefined)

    try {
      const hash = await action()

      setTxHash(hash)
      setStatus(`${label} submitted`)

      await waitForTransactionReceipt(wagmiConfig, {
        hash,
        chainId: wagmiChainId,
      })

      setStatus(`${label} confirmed`)
      refreshReads()
    }
    catch (err) {
      const message = err instanceof Error ? err.message : 'Transaction failed'

      setError(message)
      setStatus(undefined)
    }
  }

  const handleApprove = () => {
    runTx('Approve', () => writeContract.writeContractAsync({
      address: tokenAddress,
      abi: erc20Abi,
      functionName: 'approve',
      args: [ lpAddress, maxUint256 ],
    }))
  }

  const handleAddLiquidity = () => {
    runTx('Add liquidity', () => writeContract.writeContractAsync({
      address: lpAddress,
      abi: lpAbi,
      functionName: 'addLiquidity',
      args: [ amountRaw, hookData as `0x${string}` ],
    }))
  }

  const handleWithdraw = () => {
    runTx('Withdraw liquidity', () => writeContract.writeContractAsync({
      address: lpAddress,
      abi: lpAbi,
      functionName: 'withdrawLiquidity',
      args: [ depositIdRaw!, withdrawPercentRaw ],
    }))
  }

  const addDisabled = !amountRaw || !isAllowanceEnough || !isBalanceEnough || isBelowMinDeposit || !isRightNetwork || !hookData.startsWith('0x')
  const withdrawDisabled = !depositIdRaw || !withdrawPercentRaw || withdrawPercentRaw > fixedMathOne || !isDepositOwner || !isRightNetwork

  return (
    <div className="py-4">
      <div className="mb-4 flex flex-col gap-2 ds:flex-row ds:items-center ds:justify-between">
        <div>
          <h1 className="text-heading-h4 font-bold text-grey-90">Liquidity</h1>
          <p className="mt-1 text-caption-13 text-grey-60">Add or withdraw pool liquidity on the selected chain.</p>
        </div>
        <a
          className="flex items-center text-caption-13 text-grey-60 hover:text-grey-90"
          href={`${appChain.blockExplorers?.default.url}/address/${lpAddress}`}
          rel="noreferrer"
          target="_blank"
        >
          LP contract
          <Icon className="ml-1 size-4" name="interface/external_link" />
        </a>
      </div>

      <div className="grid gap-2 ds:grid-cols-2">
        <Stat label="Network" value={appChain.name} />
        <Stat label="Pool token" value={`${betToken.symbol} (${betToken.decimals} decimals)`} />
        <Stat label="Your balance" value={`${formatAmount(balanceQuery.data, betToken.decimals)} ${betToken.symbol}`} />
        <Stat label="Total pool reserve" value={`${formatAmount(reserveQuery.data as bigint | undefined, betToken.decimals)} ${betToken.symbol}`} />
        <Stat label="Minimum deposit" value={`${formatAmount(minDepoQuery.data as bigint | undefined, betToken.decimals)} ${betToken.symbol}`} />
        <Stat label="Your LP NFTs" value={String(lpNftBalanceQuery.data ?? '-')} />
      </div>

      {
        isManagerEnabled && (
          <div className="mt-3 rounded-md border border-accent-yellow/40 bg-accent-yellow/10 p-3 text-caption-13 text-grey-80">
            This pool has a liquidity manager. Add-liquidity transactions may require manager hook data in the data field.
          </div>
        )
      }

      <div className="mt-4 grid gap-3 ds:grid-cols-2">
        <section className="rounded-md border border-grey-10 bg-bg-l2 p-3">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-caption-14 font-bold text-grey-90">Add Liquidity</h2>
            <Icon className="size-5 text-brand-50" name="interface/deposit" />
          </div>

          <div className="space-y-3">
            <Input
              value={amount}
              placeholder={`Amount in ${betToken.symbol}`}
              rightNode={<span className="ml-2 text-caption-13 font-semibold text-grey-60">{betToken.symbol}</span>}
              isError={Boolean(amountRaw && (!isBalanceEnough || isBelowMinDeposit))}
              onChange={setAmount}
            />

            <Input
              value={hookData}
              placeholder="0x"
              regExp="^0x[0-9a-fA-F]*$"
              onChange={setHookData}
            />

            <div className="grid grid-cols-2 gap-2">
              <ConnectButtonWrapper>
                <Button
                  size={40}
                  style="secondary"
                  disabled={!amountRaw || isAllowanceEnough || !isBalanceEnough || !isRightNetwork}
                  loading={writeContract.isPending && status?.startsWith('Approve')}
                  onClick={handleApprove}
                >
                  Approve
                </Button>
              </ConnectButtonWrapper>

              <ConnectButtonWrapper>
                <Button
                  size={40}
                  disabled={addDisabled}
                  loading={writeContract.isPending && status?.startsWith('Add')}
                  onClick={handleAddLiquidity}
                >
                  Add
                </Button>
              </ConnectButtonWrapper>
            </div>

            <div className="text-caption-12 text-grey-60">
              Allowance: {formatAmount(allowanceQuery.data, betToken.decimals)} {betToken.symbol}
            </div>
            {
              Boolean(amountRaw && !isBalanceEnough) && (
                <div className="text-caption-12 text-accent-red">Insufficient {betToken.symbol} balance.</div>
              )
            }
            {
              Boolean(isBelowMinDeposit) && (
                <div className="text-caption-12 text-accent-red">
                  Amount is below the pool minimum deposit.
                </div>
              )
            }
          </div>
        </section>

        <section className="rounded-md border border-grey-10 bg-bg-l2 p-3">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-caption-14 font-bold text-grey-90">Withdraw Liquidity</h2>
            <Icon className="size-5 text-brand-50" name="interface/withdraw" />
          </div>

          <div className="space-y-3">
            <Input
              value={depositId}
              placeholder="LP NFT deposit ID"
              regExp="^(|0|[1-9][0-9]*)$"
              onChange={setDepositId}
            />

            <Input
              value={withdrawPercent}
              placeholder="Withdraw percent"
              maxValue="100"
              rightNode={<span className="ml-2 text-caption-13 font-semibold text-grey-60">%</span>}
              isError={withdrawPercentRaw > fixedMathOne}
              onChange={setWithdrawPercent}
            />

            <div className="grid grid-cols-3 gap-2">
              {[ '25', '50', '100' ].map((value) => (
                <Button
                  key={value}
                  size={32}
                  style={withdrawPercent === value ? 'primary' : 'tertiary'}
                  onClick={() => setWithdrawPercent(value)}
                >
                  {value}%
                </Button>
              ))}
            </div>

            <ConnectButtonWrapper>
              <Button
                className="w-full"
                size={40}
                disabled={withdrawDisabled}
                loading={writeContract.isPending && status?.startsWith('Withdraw')}
                onClick={handleWithdraw}
              >
                Withdraw
              </Button>
            </ConnectButtonWrapper>

            <div className="grid grid-cols-2 gap-2 text-caption-12">
              <div className="rounded-sm bg-bg-l1 p-2">
                <div className="text-grey-60">Withdrawable</div>
                <div className="mt-1 font-semibold text-grey-90">
                  {formatAmount(withdrawableQuery.data as bigint | undefined, betToken.decimals)} {betToken.symbol}
                </div>
              </div>
              <div className="rounded-sm bg-bg-l1 p-2">
                <div className="text-grey-60">Unlock time</div>
                <div className="mt-1 font-semibold text-grey-90">
                  {
                    withdrawAfterQuery.data
                      ? new Date(Number(withdrawAfterQuery.data) * 1000).toLocaleString()
                      : '-'
                  }
                </div>
              </div>
            </div>

            {
              Boolean(depositId && depositOwnerQuery.data && !isDepositOwner) && (
                <div className="text-caption-12 text-accent-red">
                  Connected wallet does not own this LP NFT.
                </div>
              )
            }
          </div>
        </section>
      </div>

      {
        Boolean(status || txHash || error) && (
          <div
            className={cx('mt-4 rounded-md border p-3 text-caption-13', {
              'border-accent-red/50 bg-accent-red/10 text-accent-red': error,
              'border-grey-10 bg-bg-l2 text-grey-80': !error,
            })}
          >
            {error || status}
            {
              txHash && (
                <a
                  className="ml-2 inline-flex items-center text-grey-60 hover:text-grey-90"
                  href={`${appChain.blockExplorers?.default.url}/tx/${txHash}`}
                  rel="noreferrer"
                  target="_blank"
                >
                  {formatHash(txHash)}
                  <Icon className="ml-1 size-4" name="interface/external_link" />
                </a>
              )
            }
          </div>
        )
      }

      <div className="mt-4 rounded-md border border-grey-10 bg-bg-l2 p-3 text-caption-12 text-grey-60">
        New LP deposits receive an LP NFT. Deposits can be exposed to open market losses immediately, while profit exposure starts with markets created after the deposit.
      </div>
    </div>
  )
}

export default LiquidityPage
